import { Button, Divider, Flex, Group, Text } from "@mantine/core";
import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { useFormContext } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useIsNewEntry, usePostApi, useTheme } from "src/hooks";
import { type ICheckList, type ICheckListItem } from "src/interfaces";
import { useSelector } from "src/redux/hooks";
import { pushNewEntryActionsData } from "src/redux/slices/entrySlice";
import { setCheckList } from "src/redux/slices/generatorSlice";

import ChecklistDescription from "../../../../components/shared/ChecklistDescription";
import { triggerAutosaveForExternalChange } from "../../../hooks/autosaveScheduler";
import { getHistoryRecordName } from "../checklists.utils";
import CheckListsFormItem from "./CheckListsFormItem";
import { DateTimeDisp } from "src/components/shared/DateTime";
interface Props extends ICheckList {
  yachtid: string | any;
  reset: () => void;
  disabled?: boolean;
  close: () => void;
  selectedItemIndex: number | null;
  renderedDesc: string;
}

const CheckListsForm = forwardRef(
  (
    {
      reset,
      disabled,
      close,
      selectedItemIndex,
      renderedDesc,
      ...props
    }: Props,
    ref,
  ) => {
    const dispatch = useDispatch();
    const { colors } = useTheme();

    const setValue = useFormContext()?.setValue;
    const id = useSelector(
      state => state.generatorSlice.checkListsModalProps.id,
    );
    const [selectedCheckList, setSelecedCheckList] = useState(props);
    const { post } = usePostApi();
    const isNewEntry = useIsNewEntry();
    const checks = useMemo(
      () =>
        selectedItemIndex !== null
          ? (
              selectedCheckList.data.list[selectedItemIndex] as unknown as {
                items: ICheckListItem[];
              }
            ).items
          : selectedCheckList.data.list,
      [selectedItemIndex, selectedCheckList],
    );

    const postUpdateChecklist = useCallback(
      (action: string, itemName: string, cb: any) => {
        if (props.currentEntryId && !isNewEntry) {
          void post({
            request: "record-action-checklist",
            body: {
              params: {
                cid: props.cid,
                rid: props.currentEntryId,
                lbid: props.currentLogbook,
                yachtid: props.yachtid,
                action,
                item: itemName,
              },
            },
            onSuccess: res => {
              if (cb) cb(res);
            },
          });
        } else {
          dispatch(
            pushNewEntryActionsData({
              cid: props.cid,
              rid: id ?? "",
              lbid: props.currentLogbook ?? "",
              yachtid: props.yachtid,
              action,
              item: itemName,
              url: "record-action-checklist",
            }),
          );
        }
      },
      [
        props.cid,
        props.currentEntryId,
        props.currentLogbook,
        props.yachtid,
        dispatch,
        id,
        isNewEntry,
        post,
      ],
    );

    const postCompleteChecklist = useCallback(
      (itemName: string) => {
        setTimeout(() => {
          // Only continue if this is an existing entry
          if (props.currentEntryId && !isNewEntry) {
            void post({
              request: "complete-checklist",
              body: {
                params: {
                  cid: props.cid,
                  rid: props.currentEntryId,
                  lbid: props.currentLogbook,
                  yachtid: props.yachtid,
                  item: itemName,
                },
              },
              onSuccess: res => {},
            });
          } else {
            dispatch(
              pushNewEntryActionsData({
                url: "complete-checklist",
                cid: props.cid,
                rid: id ?? "",
                lbid: props.currentLogbook ?? "",
                yachtid: props.yachtid,
                item: itemName,
              }),
            );
          }
        }, 1500);
      },
      [
        props.cid,
        props.currentEntryId,
        props.currentLogbook,
        props.yachtid,
        dispatch,
        id,
        isNewEntry,
        post,
      ],
    );

    const handleSubmit = useCallback(() => {
      if (disabled) return;

      if (selectedCheckList?.data) {
        let checked: number = 0;
        let all: number = 0;
        let reqSent: number = 0;
        let reqComplete: number = 0;

        const finalCheck = (item: string) =>
          reqSent === reqComplete &&
          checked === all &&
          postCompleteChecklist(item);

        checks.forEach(
          (v, k) =>
            (v.changed || v.naChanged) &&
            (v.unit === "check" || v.unit === "text") &&
            reqSent++,
        );

        checks.forEach(item => {
          all++;
          if (
            item.value ||
            item.notAvailable ||
            (typeof item.value === "string" && item.value.trim().length > 0) ||
            item.unit === "header"
          ) {
            checked++;
          }

          if (
            (item.changed || item.naChanged) &&
            props.currentEntryId &&
            props.currentEntryId !== "new"
          ) {
            const name =
              selectedItemIndex !== null
                ? selectedCheckList.name +
                  " - " +
                  selectedCheckList.data.list[selectedItemIndex].name
                : selectedCheckList.name;

            postUpdateChecklist(
              getHistoryRecordName(item),
              `${name} :\n ${item.text}`,
              () => {
                reqComplete++;
                void finalCheck(name);
              },
            );
          }
        });
      }

      dispatch(setCheckList(selectedCheckList));
      triggerAutosaveForExternalChange(dispatch);

      if (id) {
        setValue(id, {
          name: selectedCheckList.name,
          cid: selectedCheckList.cid,
          selectedIndex: selectedItemIndex,
        });
      }
    }, [
      disabled,
      selectedCheckList,
      dispatch,
      id,
      checks,
      postCompleteChecklist,
      props.currentEntryId,
      selectedItemIndex,
      postUpdateChecklist,
      setValue,
    ]);

    useImperativeHandle(ref, () => ({
      save() {
        handleSubmit();
      },
    }));

    const isDagon = props.yachtid === "6621c93a-fff9-11ef-9167-02c1fe2db5a5";

    return (
      <>
        <DateTimeDisp
          color={colors.grayText[0]}
          dir="row"
          size="sm"
          utc={selectedCheckList?.createddate}
          lt={selectedCheckList?.createddateLT}
          containerProps={{ mb: 10 }}
        />
        <Text fw={500} fz="sm" c="grayText.0" fs="italic">
          Description
        </Text>
        <ChecklistDescription desc={renderedDesc} styles={{ opacity: 0.7 }} />
        <Divider />

        <Flex direction="column" gap="sm" mt="sm">
          {checks.map((item, index) => (
            <CheckListsFormItem
              key={`checklist-item-${index}`}
              item={item}
              itemIndex={index}
              disabled={disabled}
              selectedItemIndex={selectedItemIndex}
              setSelecedCheckList={setSelecedCheckList}
              reverseNaOrder={isDagon}
            />
          ))}
        </Flex>
        {!disabled && (
          <Group justify="flex-end" mt="sm">
            <Button
              onClick={() => {
                close();
              }}>
              Save
            </Button>
          </Group>
        )}
      </>
    );
  },
);

export default CheckListsForm;
